import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { RaptorLogo } from '@/components/RaptorLogo';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { toast } from 'sonner';

export const LoginPage: React.FC = () => {
  const { login, verify2fa, send2faPhone, resend2fa, user, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  // 2FA step state
  const [stage, setStage] = useState<'password' | 'enroll' | 'code'>('password');
  const [mfaToken, setMfaToken] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [phoneHint, setPhoneHint] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');

  if (loading) return <div className="min-h-[80vh] flex items-center justify-center">Loading…</div>;
  if (user) return <Navigate to={user.must_reset_password ? '/force-reset' : (user.context === 'broker' ? '/broker' : '/insurer')} replace />;

  const errMsg = (err: unknown, fallback: string): string =>
    (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? fallback;

  const goDashboard = (u: { must_reset_password: boolean; context: string }): void => {
    toast.success('Welcome back');
    if (u.must_reset_password) nav('/force-reset', { replace: true });
    else nav(u.context === 'broker' ? '/broker' : '/insurer', { replace: true });
  };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(email, password);
      if (res.status === 'ok') { goDashboard(res.user); return; }
      // 2FA required
      setMfaToken(res.mfaToken);
      if (res.phoneOnFile) {
        setChallengeId(res.challengeId || '');
        setPhoneHint(res.phoneHint || '');
        setStage('code');
        toast.success(`We sent a code to ${res.phoneHint || 'your phone'}`);
      } else {
        setStage('enroll');
      }
    } catch (err: unknown) {
      toast.error(errMsg(err, 'Login failed'));
    } finally { setBusy(false); }
  };

  const submitPhone = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await send2faPhone(mfaToken, phone);
      setChallengeId(r.challengeId);
      setPhoneHint(r.phoneHint);
      setStage('code');
      toast.success(`We sent a code to ${r.phoneHint}`);
    } catch (err: unknown) {
      toast.error(errMsg(err, 'Could not send the code'));
    } finally { setBusy(false); }
  };

  const submitCode = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await verify2fa(mfaToken, challengeId, code);
      goDashboard(u);
    } catch (err: unknown) {
      toast.error(errMsg(err, 'Invalid code'));
    } finally { setBusy(false); }
  };

  const onResend = async (): Promise<void> => {
    try {
      const r = await resend2fa(mfaToken);
      setChallengeId(r.challengeId);
      toast.success('A new code is on its way');
    } catch (err: unknown) {
      toast.error(errMsg(err, 'Could not resend the code'));
    }
  };

  return (
    <div className="flex-1 min-h-[calc(100vh-32px)] grid md:grid-cols-2">
      <div className="relative overflow-hidden hidden md:flex flex-col justify-between p-10 bg-white border-r border-raptor-neutral">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
          <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full raptor-gradient blur-3xl" />
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-raptor-cyan blur-3xl" />
        </div>
        <div className="relative z-10 inline-flex items-center justify-center rounded-2xl bg-white/60 backdrop-blur-sm border border-raptor-navy/5 shadow-[0_10px_30px_-15px_rgba(6,25,53,0.25)] px-6 py-4 self-start">
          <RaptorLogo size={64} slot="login_logo" />
        </div>
        <div className="relative z-10 max-w-md">
          <h2 className="font-headline text-raptor-navy text-4xl tracking-wide leading-tight">Digital Risk Assessments,<br />Delivered.</h2>
          <p className="text-raptor-navy/70 font-body mt-4">Purpose-built for our binder programme, it combines geofenced on-site surveys, versioned inspection templates and a fully audited paper trail that covers the full lifecycle, from the first broker introduction through to renewal.</p>
          <div className="mt-6 flex gap-2 flex-wrap">
            <span className="px-3 py-1 rounded-full bg-raptor-blue/10 text-raptor-navy text-xs font-tabular border border-raptor-blue/20">End-to-end portfolio view</span>
            <span className="px-3 py-1 rounded-full bg-raptor-blue/10 text-raptor-navy text-xs font-tabular border border-raptor-blue/20">Server-enforced permissions</span>
            <span className="px-3 py-1 rounded-full bg-raptor-blue/10 text-raptor-navy text-xs font-tabular border border-raptor-blue/20">Independent of any single supplier</span>
          </div>
        </div>
        <div className="text-raptor-navy/50 text-xs font-body relative z-10">© Raptor Platform</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-lg">
              {stage === 'password' ? 'Sign in to Raptor' : stage === 'enroll' ? 'Set up two-factor' : 'Enter your code'}
            </CardTitle>
            <CardDescription>
              {stage === 'password' && 'For staff and appointed brokers. New brokers should register using the invitation link sent by email, rather than from this page.'}
              {stage === 'enroll' && 'For your security, every account is protected with a one-time SMS code. Add your mobile number to receive it.'}
              {stage === 'code' && `We've sent a 6-digit code by SMS to ${phoneHint || 'your phone'}. Enter it below to finish signing in.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {stage === 'password' && (
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
                </div>
                <Button data-testid="login-submit" type="submit" variant="gradient" className="w-full" disabled={busy}>
                  {busy ? 'Signing in…' : 'Sign in'}
                </Button>
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => nav('/forgot-password')}
                    className="text-xs text-raptor-navy/60 hover:text-raptor-blue font-body"
                    data-testid="login-forgot-link"
                  >
                    Forgot password?
                  </button>
                </div>
              </form>
            )}

            {stage === 'enroll' && (
              <form onSubmit={submitPhone} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Mobile number</Label>
                  <Input id="phone" data-testid="login-2fa-phone" type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 082 123 4567" autoComplete="tel" />
                </div>
                <Button data-testid="login-2fa-send" type="submit" variant="gradient" className="w-full" disabled={busy}>
                  {busy ? 'Sending…' : 'Send code'}
                </Button>
              </form>
            )}

            {stage === 'code' && (
              <form onSubmit={submitCode} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="code">6-digit code</Label>
                  <Input id="code" data-testid="login-2fa-code" inputMode="numeric" autoFocus required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="______" autoComplete="one-time-code" />
                </div>
                <Button data-testid="login-2fa-verify" type="submit" variant="gradient" className="w-full" disabled={busy}>
                  {busy ? 'Verifying…' : 'Verify & sign in'}
                </Button>
                <div className="flex items-center justify-between">
                  <button type="button" onClick={() => { setStage('password'); setCode(''); }} className="text-xs text-raptor-navy/60 hover:text-raptor-blue font-body" data-testid="login-2fa-back">
                    Start over
                  </button>
                  <button type="button" onClick={onResend} className="text-xs text-raptor-navy/60 hover:text-raptor-blue font-body" data-testid="login-2fa-resend">
                    Resend code
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
